/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

/*mobile footer menu*/

document.addEventListener('DOMContentLoaded', function() {
  function toggleAccordion(title) {
    const menu = title.nextElementSibling;
    const toggle = title.querySelector('.footer-menu-toggle');
    const downArrowSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="5" fill="none"><path fill="#3C3C3C" d="M5 5 0 0h10L5 5Z"/></svg>';
    const leftArrowSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="5" height="10" fill="none"><path fill="#3C3C3C" d="M0 5l5 5V0l-5 5Z"/></svg>';
    if (getComputedStyle(menu).display === 'block') {
      menu.style.display = 'none';
      toggle.innerHTML = leftArrowSvg;
    } else {
      menu.style.display = 'block';
      const height = menu.scrollHeight + 'px';
      menu.style.height = height;
      toggle.innerHTML = downArrowSvg;
    }
  }

  document.addEventListener('click', function(e) {
    if (e.target.closest('.footer-menu-heading ') || e.target.closest('.footer-menu-toggle svg')) {
      const titleElement = e.target.closest('.footer-menu-heading ');
      if (window.innerWidth <= 768) {
        toggleAccordion(titleElement);
      }
    }
  });

  function adjustForScreenWidth() {
    const isMobile = window.innerWidth <= 768;
    const downArrowSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="5" fill="none"><path fill="#ffffff" d="M5 5 0 0h10L5 5Z"/></svg>';
    const leftArrowSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="5" height="10" fill="none"><path fill="#ffffff" d="M0 5l5 5V0l-5 5Z"/></svg>';
    document.querySelectorAll('.section-footer__menu-block').forEach(function(block) {
      const menu = block.querySelector('.section-footer__menu-block__menu');
      const toggle = block.querySelector('.footer-menu-toggle');
      menu.style.display = isMobile ? 'none' : 'block';
      toggle.innerHTML = isMobile ? leftArrowSvg : downArrowSvg;
      if (!isMobile) {
        menu.style.height = 'auto';
      }
    });
  }

  adjustForScreenWidth();
  let resizeTimer;
  window.addEventListener('resize', function() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(adjustForScreenWidth, 250);
  });
});

/*tabbed*/
document.addEventListener('DOMContentLoaded', function() {
  // Select all custom scrollbar containers
  const scrollbarContainers = document.querySelectorAll('.custom-scrollbar-container');

  scrollbarContainers.forEach((scrollbarContainer) => {
    // Assuming the carousel is the immediately preceding sibling of the scrollbar container
    const carousel = scrollbarContainer.previousElementSibling;
    const slider = carousel.querySelector('.slider--no-scrollbar');
    const scrollbarThumb = scrollbarContainer.querySelector('.custom-scrollbar-thumb');

    if (!slider || !scrollbarThumb) return; // Skip if no slider or scrollbar thumb found

    function initCustomScrollbar() {
      const scrollPercentage = slider.offsetWidth / slider.scrollWidth;
      scrollbarThumb.style.width = `${scrollPercentage * 100}%`;
    }

    function syncCustomScrollbar() {
      const scrollPercentage = slider.scrollLeft / (slider.scrollWidth - slider.clientWidth);
      scrollbarThumb.style.left = `${scrollPercentage * (scrollbarContainer.offsetWidth - scrollbarThumb.offsetWidth)}px`;
    }

    // Initialize and sync scrollbar on load and resize
    initCustomScrollbar();
    window.addEventListener('resize', initCustomScrollbar);
    slider.addEventListener('scroll', syncCustomScrollbar);
  });
});

  function moveHeaderLocalization() {
    var headerLocalization = document.querySelector('a.header-localization');
    var mobileHeaderLocalization = document.querySelector('.mobile-header-localization');
    var screenWidth = window.innerWidth;

    if (screenWidth < 1200) {
      if (headerLocalization && mobileHeaderLocalization) {
        mobileHeaderLocalization.appendChild(headerLocalization);
      }
    } else {
      if (mobileHeaderLocalization && mobileHeaderLocalization.contains(headerLocalization)) {
        document.querySelector('.header-localization-container').appendChild(headerLocalization);
      }
    }
  }

  // Initial check and setup event listener for resizing
  moveHeaderLocalization();
  window.addEventListener('resize', moveHeaderLocalization);

  // Swatches Slider
document.addEventListener('DOMContentLoaded', function() {
  function initializeSwatchSliders() {
    var swatchSliders = document.querySelectorAll('.swiper-container');

    swatchSliders.forEach(function(swatchSlider) {
      if (!swatchSlider.classList.contains('swiper-initialized')) {
        var swiper = new Swiper(swatchSlider, {
          slidesPerView: 3,
          spaceBetween: 0,
          navigation: {
            nextEl: swatchSlider.querySelector('.swiper-button-next'),
            prevEl: swatchSlider.querySelector('.swiper-button-prev'),
          },
        });
        swatchSlider.classList.add('swiper-initialized');
      }
    });
  }

  // Initial initialization
  initializeSwatchSliders();

  // Use MutationObserver to detect changes in the DOM
  var observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.addedNodes.length > 0) {
        initializeSwatchSliders();
      }
    });
  });

  // Observe the entire document for changes
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
});
//truncated titles
function initializeTitleTruncation() {
    var productTitles = document.querySelectorAll('.product-block .product-block__title');

    function handleTitleDisplay() {
        productTitles.forEach(function(title) {
            if (window.innerWidth <= 768) { // Mobile screens
                setupScrollingTitle(title);
            } else { // Desktop screens
                setupHoverTitle(title);
            }
        });
    }

    function setupScrollingTitle(title) {
        title.classList.remove('full');
        title.removeEventListener('mouseover', toggleFullTitle);
        title.removeEventListener('mouseout', toggleFullTitle);

        if (title.scrollWidth > title.clientWidth) {
            let wrapper = title.parentElement;
            if (!wrapper.classList.contains('title-scroll-wrapper')) {
                wrapper = document.createElement('div');
                wrapper.className = 'title-scroll-wrapper';
                title.parentNode.insertBefore(wrapper, title);
                wrapper.appendChild(title);
            }

            const scrollAmount = title.scrollWidth - title.clientWidth;
            const speed = 50; // pixels per second
            const pauseDuration = 2; // seconds
            const scrollDuration = scrollAmount / speed;
            const totalDuration = (scrollDuration + pauseDuration) * 2;

            title.style.setProperty('--scroll-amount', `${scrollAmount}px`);
            title.style.setProperty('--total-duration', `${totalDuration}s`);
            title.style.setProperty('--scroll-duration', `${scrollDuration}s`);
            title.style.setProperty('--pause-duration', `${pauseDuration}s`);

            let observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        title.classList.add('scrolling');
                    } else {
                        title.classList.remove('scrolling');
                    }
                });
            }, { threshold: 0 });
            observer.observe(wrapper);
        }
    }

    function setupHoverTitle(title) {
        title.classList.remove('full');
        title.addEventListener('mouseover', toggleFullTitle);
        title.addEventListener('mouseout', toggleFullTitle);
    }

    function toggleFullTitle(event) {
        if (event.type === 'mouseover') {
            this.classList.add('full');
        } else if (event.type === 'mouseout') {
            this.classList.remove('full');
        }
    }

    handleTitleDisplay();
    window.addEventListener('resize', handleTitleDisplay);
}

// Initialize the script on initial page load
document.addEventListener('DOMContentLoaded', function() {
    initializeTitleTruncation();

    // Use MutationObserver to detect changes in the DOM
    var observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
            if (mutation.addedNodes.length > 0) {
                initializeTitleTruncation();
            }
        });
    });

    // Observe the entire document for changes
    observer.observe(document.body, {
        childList: true,
        subtree: true
    });
});


document.addEventListener('DOMContentLoaded', function() {
  // Check if we are on the main product page
  const mainProductPage = document.querySelector('.section-main-product');
  if (!mainProductPage) {
    return; // Exit if not on the main product page
  }

  const complementaryProducts = document.querySelector('.complementary-products');
  const complementaryMobile = document.querySelector('.complementary-mobile');
  const complementaryDesktop = document.querySelector('.complementary-desktop');

  // Ensure all elements are found
  if (!complementaryProducts || !complementaryMobile || !complementaryDesktop) {
    console.error('One or more elements not found:', {
      complementaryProducts,
      complementaryMobile,
      complementaryDesktop
    });
    return;
  }

  let isMovedToDesktop = false;

  function moveComplementaryProducts() {
    if (window.innerWidth >= 768) { // Adjust the breakpoint as needed
      if (!isMovedToDesktop) {
        complementaryDesktop.appendChild(complementaryProducts);
        isMovedToDesktop = true;
      }
    } else {
      if (isMovedToDesktop) {
        complementaryMobile.appendChild(complementaryProducts);
        isMovedToDesktop = false;
      }
    }
  }

  // Initial move
  moveComplementaryProducts();

  // Move on window resize
  window.addEventListener('resize', moveComplementaryProducts);
});


/*Mobile Collections Slider*/

document.addEventListener('DOMContentLoaded', function() {
  const collectionListSection = document.querySelector('.shopify-section-group-header-group.section-collection-list');
  const mobileNavDrawerCollectionList = document.querySelector('.mobile-navigation-drawer__collection-list');

  if (collectionListSection && mobileNavDrawerCollectionList) {
    mobileNavDrawerCollectionList.appendChild(collectionListSection);
  } else {
    console.error('One or more elements not found:', {
      collectionListSection,
      mobileNavDrawerCollectionList
    });
  }
});

function updateMainSearchPosition() {
  const mainSearch = document.querySelector('.main-search');
  const header = document.querySelector('.section-header');
  const announcementBar = document.querySelector('.section-announcement-bar');

  if (mainSearch && header) {
    let topOffset = 0;

    // Check if the header is visible and add its height
    if (header.offsetHeight > 0 && window.getComputedStyle(header).display !== 'none') {
      topOffset += header.offsetHeight;
    }

    // Check if the announcement bar is visible and add its height
    if (announcementBar && announcementBar.offsetHeight > 0 && window.getComputedStyle(announcementBar).display !== 'none') {
      if (window.scrollY < announcementBar.offsetHeight) {
        topOffset += announcementBar.offsetHeight - window.scrollY;
      }
    }

    mainSearch.style.top = `${topOffset}px`;
  }
}

// Run the function initially
updateMainSearchPosition();

// Add event listener for window resize
window.addEventListener('resize', updateMainSearchPosition);

// Add event listener for window scroll
// window.addEventListener('scroll', updateMainSearchPosition);

// Add event listener for show-search-link clicks
document.querySelectorAll('.show-search-link').forEach(function(link) {
  link.addEventListener('click', updateMainSearchPosition);
});



/*Collections Slider Active Class*/
document.addEventListener('DOMContentLoaded', function() {
    // Function to add active class and move active block to the desired position
    function addActiveClassToProductBlock() {
        // Get the current page URL
        var currentUrl = window.location.pathname;
        // console.log('Current URL:', currentUrl);

        // Get all product blocks
        var productBlocks = document.querySelectorAll('#content .collection-listing.slider .product-block');
        // console.log('Product Blocks:', productBlocks);

        var activeSliderItem = null;
        var firstSliderItem = null;
        var firstSliderItemContent = null;

        // Loop through each product block
        productBlocks.forEach(function(block, index) {
            var link = block.querySelector('.product-link');
            if (index === 0) {
                firstSliderItem = block.closest('.slider__item');
                if (firstSliderItem) {
                    firstSliderItem.classList.add('first-slide');
                    firstSliderItemContent = firstSliderItem.innerHTML;
                    // console.log('First Slider Item:', firstSliderItem);
                }
            }
            if (link && link.getAttribute('href') === currentUrl) {
                block.classList.add('active');
                // console.log('Active Block:', block);
                activeSliderItem = block.closest('.slider__item');
                if (activeSliderItem) {
                    activeSliderItem.classList.add('active');
                    // console.log('Active Slider Item:', activeSliderItem);
                }
            }
        });

        // Add a new slide next to the first slide
        if (activeSliderItem && firstSliderItem && firstSliderItemContent && activeSliderItem !== firstSliderItem) {
            var newSliderItem = document.createElement('div');
            newSliderItem.classList.add('slider__item');
            newSliderItem.innerHTML = firstSliderItemContent;

            firstSliderItem.parentElement.insertBefore(newSliderItem, firstSliderItem.nextSibling);
            // console.log('Inserted new slider item with the first slide content next to the first slide');

            // Move the content of the active slide into the old first slide
            firstSliderItem.innerHTML = activeSliderItem.innerHTML;
            // console.log('Moved the content of the active slide into the old first slide');

            // Remove the old active slide
            activeSliderItem.remove();
            // console.log('Removed the old active slide');

            // Move the first slider item (now containing the active slide content) to the first position
            var sliderGrid = document.querySelector('#content .collection-listing.slider .product-grid.product-grid--carousel.slider__grid');
            // console.log('Slider Grid:', sliderGrid);
            if (sliderGrid && sliderGrid.contains(firstSliderItem)) {
                sliderGrid.insertBefore(firstSliderItem, sliderGrid.firstChild);
                // console.log('Moved the first slider item (now containing the active slide content) to the beginning');
            } else {
                // console.log('Slider grid not found or does not contain the first slider item.');
            }
        } else {
            // console.log('No active slider item or first slider item found or they are the same.');
        }
    }

    // Check if the specific slider exists to prevent errors
    var sliderGrid = document.querySelector('#content .collection-listing.slider .product-grid.product-grid--carousel.slider__grid');
    if (sliderGrid) {
        addActiveClassToProductBlock();
    } else {
        console.log('Slider grid not found.');
    }
});


// // Store the original parent and next sibling of the utility bar
// const utilityBar = document.querySelector('.template-collection .utility-bar');
// const originalParent = utilityBar ? utilityBar.parentNode : null;
// const originalNextSibling = utilityBar ? utilityBar.nextSibling : null;

// function moveUtilityBar() {
//   const pageHeader = document.querySelector('.template-collection .page-header');
  
//   if (utilityBar && pageHeader) {
//     if (window.innerWidth < 1000) {
//       // Move utility-bar under page-header
//       pageHeader.parentNode.insertBefore(utilityBar, pageHeader.nextSibling);
//     } else {
//       // Move utility-bar back to its original position
//       if (originalParent) {
//         originalParent.insertBefore(utilityBar, originalNextSibling);
//       }
//     }
//   }
// }

// // Run the function initially
// moveUtilityBar();

// // Add event listener for window resize
// window.addEventListener('resize', moveUtilityBar);


/*swatches active*/
document.addEventListener('DOMContentLoaded', function() {
  // Get all swatch wrappers
  const swatchWrappers = document.querySelectorAll('.swatch-slider');

  swatchWrappers.forEach(function(wrapper) {
    // Get all swatches within this wrapper
    const swatches = wrapper.querySelectorAll('.product-block-options__item');

    swatches.forEach(function(swatch) {
      // Add click event listener to each swatch
      swatch.addEventListener('click', function() {
        // Remove 'active' class from all swatches in this wrapper
        swatches.forEach(function(s) {
          s.classList.remove('active');
        });
        // Add 'active' class to the clicked swatch
        swatch.classList.add('active');
      });

      // Add mouseover event listener to each swatch
      swatch.addEventListener('mouseover', function() {
        // Remove 'active' class from all swatches in this wrapper
        swatches.forEach(function(s) {
          s.classList.remove('active');
        });
      });
    });
  });
});

// /* default to xs */
document.addEventListener('DOMContentLoaded', function() {
  function selectDefaultVariant(context) {
    var urlParams = new URLSearchParams(window.location.search);
    var variantIdFromUrl = urlParams.get('variant');

    if (!variantIdFromUrl) {
      var xsRadio = context.querySelector('input[name^="product-form-"][value="XS"]:not([disabled])');
      var firstAvailableRadio = context.querySelector('input[name^="product-form-"]:not([disabled])');

      if (xsRadio) {
        xsRadio.checked = true;
        updateHiddenVariantField(xsRadio, context);
      } else if (firstAvailableRadio) {
        firstAvailableRadio.checked = true;
        updateHiddenVariantField(firstAvailableRadio, context);
      }
    }
  }

  function updateHiddenVariantField(radio, context) {
    var hiddenVariantField = context.querySelector('.js-product-form input[name="id"]');
    if (hiddenVariantField) {
      console.log('Current variant ID:', hiddenVariantField.value);
      hiddenVariantField.value = radio.dataset.variantId;
      console.log('Updated variant ID:', hiddenVariantField.value);
    } else {
      console.log('Hidden variant field not found in the context');
    }

    // Trigger change and click events
    var changeEvent = new Event('change', { bubbles: true, cancelable: true });
    radio.dispatchEvent(changeEvent);

    var clickEvent = new MouseEvent('click', { view: window, bubbles: true, cancelable: true });
    radio.dispatchEvent(clickEvent);
  }

  // Function to check and select the appropriate size
  function checkAndSelectSize(context) {
    console.log('Checking and selecting size for context:', context);
    // Find all elements with class 'option-selector'
    var optionSelectors = context.querySelectorAll('.option-selector');
    optionSelectors.forEach(function(optionSelector) {
      selectDefaultVariant(optionSelector);
    });

    // If the context itself is an option selector, handle it directly
    if (context.classList && context.classList.contains('option-selector')) {
      selectDefaultVariant(context);
    }
  }

  let observer;

  function startObserver() {
    observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) { // Ensure it's an element node
            console.log('New node added:', node);
            // Check if the node or its descendants match the selector
            if (node.matches('.quickbuy-container .product-form #option-size') || node.querySelector('.quickbuy-container .product-form #option-size')) {
              console.log('Quickbuy product form size option loaded:', node);
              checkAndSelectSize(node);
              stopObserver(); // Stop observing once the desired element is found and handled
            }
          }
        });
      });
    });

    // Start observing the document body for added nodes
    observer.observe(document.body, { childList: true, subtree: true });
    console.log('Observer started');
  }

  function stopObserver() {
    if (observer) {
      observer.disconnect();
      console.log('Observer stopped');
    }
  }

  // Listen for quickbuy open event
  document.addEventListener('on:quickbuy:before-open', function() {
    console.log('Quickbuy opening');
    startObserver();
  });

  // Listen for quickbuy close event
  document.addEventListener('on:quickbuy:after-close', function() {
    console.log('Quickbuy closed');
    stopObserver();
  });

  // Additional event listener for when the quickbuy content is loaded
  document.addEventListener('on:quickbuy:after-open', function(event) {
    console.log('Quickbuy opened, checking content');
    if (event.detail && event.detail.quickbuyContent) {
      checkAndSelectSize(event.detail.quickbuyContent);
    } else {
      // Ensure default selection even if event.detail.quickbuyContent is not provided
      var quickbuyContent = document.querySelector('.quickbuy-container');
      if (quickbuyContent) {
        checkAndSelectSize(quickbuyContent);
      }
    }
  });

  // Initial selection on page load for normal product pages
  selectDefaultVariant(document);
});





document.addEventListener('DOMContentLoaded', function() {
    function updateShopPayTerms() {
        const cartTotalElement = document.querySelector('.estimated-total .money');
        const totalPriceText = cartTotalElement.textContent.replace('$', '').replace(',', '');
        const totalPrice = parseFloat(totalPriceText).toFixed(2);
        const shopifyPaymentTerms = document.querySelector('.shopify-installments shopify-payment-terms');

        if (shopifyPaymentTerms) {
            let shopifyMeta = shopifyPaymentTerms.getAttribute('shopify-meta');
            console.log("Original shopify-meta:", shopifyMeta); // Debugging log

            try {
                // Properly handle the replacement of &quot; with actual quotes
                shopifyMeta = shopifyMeta.replace(/&quot;/g, '"');
                console.log("shopify-meta after replace:", shopifyMeta); // Debugging log

                // Parse the shopify-meta string to a JSON object
                let metaObject = JSON.parse(shopifyMeta);
                console.log("Parsed metaObject:", metaObject); // Debugging log

                onProductAddedToCart();
                const tabItems = document.querySelectorAll('.tab-item');
                const tabContents = document.querySelectorAll('.cross-sell-tabs-content-item');

                tabItems.forEach(item => {
                    item.addEventListener('click', function () {
                        const tabIndex = this.getAttribute('data-tab');

                        // Remove active class from all tabs and contents
                        tabItems.forEach(el => el.classList.remove('active'));
                        tabContents.forEach(el => el.classList.remove('active'));

                        // Add active class to clicked tab and corresponding content
                        this.classList.add('active');
                        tabContents[tabIndex].classList.add('active');
                    });
                });

                // Update the full_price attribute
                metaObject.full_price = `$${totalPrice}`;

                // Convert the object back to a JSON string
                const updatedShopifyMeta = JSON.stringify(metaObject);

                // Set the updated attribute without re-encoding quotes
                shopifyPaymentTerms.setAttribute('shopify-meta', updatedShopifyMeta);

                // Console log for debugging
                // console.log("Updated shopify-meta set to attribute:", shopifyPaymentTerms.getAttribute('shopify-meta'));
            } catch (error) {
                // console.error("Error parsing shopify-meta:", error);
            }
        }
    }

    document.addEventListener('on:cart:after-merge', function(event) {
        // console.log('Cart updated:', event);
        updateShopPayTerms();
    });

    // Initial log to confirm script is running
    // console.log("Event listener for on:cart:after-merge initialized");
});

document.addEventListener('DOMContentLoaded', function() {
  var form = document.querySelector('.js-product-form');
  var addToCartButton = document.querySelector('.btn.add-to-cart');
  var hiddenField = document.querySelector('input[name="id"]');

  if (!form || !addToCartButton || !hiddenField) {
    // console.error('Required elements not found');
    return;
  }

  // Create the sold out button
  var soldOutButton = document.createElement('button');
  soldOutButton.className = 'btn--large unavailable-message sold-out';
  soldOutButton.type = 'button';
  soldOutButton.textContent = 'Notify Me';
  soldOutButton.style.display = 'none';

  // Insert the sold out button after the original add to cart button
  addToCartButton.parentNode.insertBefore(soldOutButton, addToCartButton.nextSibling);

  // Create the unavailable message div
  var unavailableMessageDiv = document.createElement('div');
  unavailableMessageDiv.textContent = 'Unavailable';
  unavailableMessageDiv.className = 'unavailable-message btn btn--large';
  unavailableMessageDiv.style.display = 'none';


  // Insert the unavailable message div after the original add to cart button
  addToCartButton.parentNode.insertBefore(unavailableMessageDiv, addToCartButton.nextSibling);
  
  function updateButtons() {
    var bisTriggerButton = form.querySelector('#BIS_trigger.btn');
    var preOrder = form.querySelector('.timesact-selling-plan-description');
    var isBISTriggerVisible = bisTriggerButton && window.getComputedStyle(bisTriggerButton).display !== 'none';
    var isHiddenFieldEmpty = !hiddenField.value;
    if (isBISTriggerVisible && hiddenField.value) {
      soldOutButton.id = 'BIS_trigger';
      addToCartButton.disabled = true;
      addToCartButton.classList.add('unavailable');
      addToCartButton.textContent = 'Sold Out';
      addToCartButton.style.display = 'none';
      soldOutButton.style.display = 'block';
      bisTriggerButton.style.opacity = '0';
      bisTriggerButton.style.position = 'absolute';
    } else if (isHiddenFieldEmpty && !isBISTriggerVisible) {
      soldOutButton.removeAttribute('BIS_trigger');
      addToCartButton.disabled = true;
      addToCartButton.classList.add('unavailable');
      addToCartButton.style.display = 'none';
      // unavailableMessageDiv.style.display = 'block';
    } else if (preOrder && !isBISTriggerVisible) {
      soldOutButton.removeAttribute('BIS_trigger');
      soldOutButton.style.display = 'none';
      unavailableMessageDiv.style.display = 'none';
      addToCartButton.disabled = false;
      addToCartButton.textContent = 'Pre-order';
      addToCartButton.style.display = 'block';
      addToCartButton.classList.remove('unavailable');
    } else {
      soldOutButton.removeAttribute('BIS_trigger');
      soldOutButton.style.display = 'none';
      unavailableMessageDiv.style.display = 'none';
      addToCartButton.disabled = false;
      addToCartButton.textContent = addToCartButton.getAttribute('data-add-to-cart-text') || 'Add to bag';
      addToCartButton.style.display = 'block';
      addToCartButton.classList.remove('unavailable');
    }
  }

  // Initial check on page load
  updateButtons();

  // Periodically check the BIS_trigger display status
  setInterval(updateButtons, 1000);

  // Observe changes to the hidden field's value
  if (window.MutationObserver) {
    var observer = new MutationObserver(updateButtons);
    observer.observe(hiddenField, { attributes: true, attributeFilter: ['value'] });
  }

  // Ensure button is checked and updated if value changes manually
  hiddenField.addEventListener('input', updateButtons);

  if (window.MutationObserver) {
    var preOrderTextObserver = new MutationObserver(function(mutationsList) {
      for (var mutation of mutationsList) {
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          // Si el texto del botón es "Pre-order", desconectamos el observer
          if (addToCartButton.textContent.trim() === 'Pre-order') {
            preOrderTextObserver.disconnect();

            setTimeout(() => {
              if(document.querySelector('.add-to-cart-sticky')){
                document.querySelector('.add-to-cart-sticky').textContent = 'Pre-order';
              }
            }, 100);
          }
        }
      }
    });

    preOrderTextObserver.observe(addToCartButton, {
      childList: true,
      characterData: true,
      subtree: true
    });
  }
});

document.addEventListener('DOMContentLoaded', function() {
    // Get the current URL path
    var path = window.location.pathname;

    // Check if the path starts with "/en-"
    if (path.startsWith('/en-')) {
        // Select all elements with the class "ge-hide"
        var elementsToHide = document.querySelectorAll('.ge-hide');

        // Loop through each element and apply "display: none"
        elementsToHide.forEach(function(element) {
            element.style.display = 'none';
        });
    }
});

document.addEventListener('DOMContentLoaded', function() {
  document.querySelectorAll('.navigation__link.navigation__column-title').forEach(function(el) {
    el.removeAttribute('href');
  });
});

document.addEventListener('DOMContentLoaded', function() {
    const tabs = document.querySelectorAll('.tab-links a');
    const tabContents = document.querySelectorAll('.tab-content .tab');
    const tabSelect = document.querySelector('.tab-select');

    tabs.forEach(tab => {
        tab.addEventListener('click', function(event) {
            event.preventDefault();
            const activeTabAttr = this.getAttribute('href');
            updateActiveTab(activeTabAttr);
        });
    });

    if (tabSelect) {
        tabSelect.addEventListener('change', function() {
            const activeTabAttr = this.value;
            updateActiveTab(activeTabAttr);
        });
    }

    function updateActiveTab(activeTabAttr) {
        tabs.forEach(el => el.parentElement.classList.remove('active'));
        const activeLink = document.querySelector(`.tab-links a[href='${activeTabAttr}']`);
        if (activeLink) {
            activeLink.parentElement.classList.add('active');
        }
        tabContents.forEach(content => content.classList.remove('active'));
        document.querySelector(activeTabAttr).classList.add('active');
        if (tabSelect) {
            tabSelect.value = activeTabAttr;
        }
    }
});
document.querySelectorAll('.order-details-btn').forEach(function(btn) {
  btn.addEventListener('click', function() {
    this.closest('.orders-item').querySelector('.order-detail-popup-overlay').style.display = 'block';
  });
});
document.querySelectorAll('.close-popup').forEach(function(btn) {
  btn.addEventListener('click', function() {
    this.closest('.order-detail-popup-overlay').style.display = 'none';
  });
});



function onProductAddedToCart() {
    setTimeout(() => {
        var cartDrawer = document.querySelector('.cart_drawer-popup-container');
        if (cartDrawer) {
            cartDrawer.style.display = 'none';
        }
    }, "1500");
}
document.addEventListener('DOMContentLoaded', function () {
    const tabItems = document.querySelectorAll('.tab-item');
    const tabContents = document.querySelectorAll('.cross-sell-tabs-content-item');

    tabItems.forEach(item => {
        item.addEventListener('click', function () {
            const tabIndex = this.getAttribute('data-tab');

            // Remove active class from all tabs and contents
            tabItems.forEach(el => el.classList.remove('active'));
            tabContents.forEach(el => el.classList.remove('active'));

            // Add active class to clicked tab and corresponding content
            this.classList.add('active');
            tabContents[tabIndex].classList.add('active');
        });
    });
});
